import React from 'react';

interface ILoaderProviderProps {
  children: React.ReactNode;
}

function noop(): void {
  throw new Error('Function not loaded.');
}

const Context = React.createContext<{
  toggleLoader: (show: boolean) => void;
  showLoader: boolean;
}>({
  toggleLoader: noop,
  showLoader: false
});

/**
 * Helper function to ensure minimum loader display time
 * @param show Current requested loader state
 * @param startTime Time when loader was first shown
 * @param minDuration Minimum duration to show loader in ms
 * @returns Object with showNow (should loader be shown now) and delay (optional delay for hide)
 */
const getLoaderState = (
  show: boolean,
  startTime: number | null,
  minDuration: number = 1000
) => {
  // If we want to show the loader, show it immediately
  if (show) {
    return { showNow: true, delay: 0 };
  }

  // If no start time recorded or already passed minimum duration
  if (!startTime) {
    return { showNow: false, delay: 0 };
  }

  const displayTime = Date.now() - startTime;

  // If displayed less than minimum duration, calculate remaining time
  if (displayTime < minDuration) {
    return { showNow: true, delay: minDuration - displayTime };
  }

  // Already displayed for minimum duration, hide immediately
  return { showNow: false, delay: 0 };
};

export const LoaderProvider = ({ children }: ILoaderProviderProps) => {
  const [showLoader, setShowLoader] = React.useState<boolean>(false);
  const loaderStartTimeRef = React.useRef<number | null>(null);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup function for timeouts
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const toggleLoader = (show: boolean) => {
    // Avoid unnecessary updates
    if (show === showLoader) {
      return;
    }

    // Clear any pending hide timeouts
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Record start time when showing
    if (show && !loaderStartTimeRef.current) {
      loaderStartTimeRef.current = Date.now();
    }

    // Get loader state based on requested show value and timing
    const { showNow, delay } = getLoaderState(show, loaderStartTimeRef.current);

    if (delay > 0) {
      // Schedule hiding after remaining time
      hideTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
        loaderStartTimeRef.current = null;
        hideTimeoutRef.current = null;
      }, delay);
    } else {
      // Update immediately
      setShowLoader(showNow);

      // Reset start time if hiding
      if (!show) {
        loaderStartTimeRef.current = null;
      }
    }
  };

  const value = React.useMemo(
    () => ({
      toggleLoader,
      showLoader
    }),
    [showLoader]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useLoader = () => {
  const context = React.useContext(Context);

  if (!context) {
    throw new Error('useLoader() can only be used within AuthProvider');
  }

  return context;
};

export default useLoader;
